import { filteredListPath } from '@components/Common/Utils/filterLinks';
import { isHrTeam, isSuperAdmin } from '@components/Common/Utils/roles';

/**
 * The queues a staff member has to act on, for the dashboard's "Needs attention" strip.
 *
 * Each queue is counted with the same filter its link applies, so the number on the tile is the
 * number of rows the click lands on. The Overview's own rule (see `Dashboard/index.tsx`) is that a
 * tile whose count disagrees with the page it opens teaches people the click is not worth making.
 *
 * `superAdminOnly` mirrors the BFF blocklist (`graphQLController.ts`), not a preference: company
 * requests, account review and the Premium Hiring contract flow are refused to an HR there, so
 * offering an HR the queue would only ever produce a 403 or a page they cannot act on.
 */
export type AttentionKey =
  | 'companyRequests'
  | 'accountReviews'
  | 'premiumRequests'
  | 'agreementsSent'
  | 'awaitingFeedback'
  | 'failedImports';

export interface AttentionQueue {
  key: AttentionKey;
  /** Tile label: what the records are, not what to do about them. */
  label: string;
  /** Accessible name for the tile's link. */
  linkLabel: string;
  to: string;
  superAdminOnly: boolean;
}

/**
 * The query param the company requests tab and the import page read to open on one status.
 *
 * Deliberately not `filter_Status`: the requests tab lives inside the Companies page, whose filter
 * panel adopts every `filter_*` param as a column of its own, and "Status" is not one of them.
 */
export const STATUS_PARAM = 'status';

export const ATTENTION_QUEUES: AttentionQueue[] = [
  {
    key: 'companyRequests',
    label: 'Company requests',
    linkLabel: 'Open the pending company requests',
    to: `/company-requests?${STATUS_PARAM}=pending`,
    superAdminOnly: true,
  },
  {
    key: 'accountReviews',
    label: 'Accounts to review',
    linkLabel: 'Open the employers waiting for account review',
    to: filteredListPath('/employers', { 'Account Review': 'PENDING' }),
    superAdminOnly: true,
  },
  {
    // Status=false: a request on an account that is already managed is not waiting for anything.
    key: 'premiumRequests',
    label: 'Contracts to send',
    linkLabel: 'Open the Premium Hiring requests waiting for a contract',
    to: filteredListPath('/employers', { 'Premium Hiring Request': 'PENDING', Status: 'false' }),
    superAdminOnly: true,
  },
  {
    key: 'agreementsSent',
    label: 'Contracts awaiting activation',
    linkLabel: 'Open the employers whose agreement was sent but not activated',
    to: filteredListPath('/employers', { 'Premium Hiring Request': 'CONTRACT_SENT', Status: 'false' }),
    superAdminOnly: true,
  },
  {
    /*
     * The stage move a recruiter is waiting to make: a submitted candidate the client has not
     * answered on. The server scopes applications to an HR's own clients, and the link runs the
     * same query, so each role sees its own number.
     */
    key: 'awaitingFeedback',
    label: 'Waiting on client feedback',
    linkLabel: 'Open the submitted candidates waiting on client feedback',
    to: filteredListPath('/applications/list', { Stage: 'Submitted', Detail: 'Feedback pending' }),
    superAdminOnly: false,
  },
  {
    // Today only, because that is the day the import page opens on.
    key: 'failedImports',
    label: 'Failed imports today',
    linkLabel: 'Open the resume imports that failed today',
    to: `/jobseekers/import?${STATUS_PARAM}=FAILED`,
    superAdminOnly: false,
  },
];

/** The queues a role may see. An unrecognised role sees none, as with the nav. */
export const queuesFor = (role?: string | null): AttentionQueue[] => {
  if (isSuperAdmin(role)) return ATTENTION_QUEUES;
  // Marketing has no queue to work: these are recruiter and review queues (owner, 2026-10-19).
  // An HR Lead works the recruiter queues as an HR does; the review and contract queues stay the
  // Super Admin's, as the operations behind them do on the BFF.
  if (isHrTeam(role)) return ATTENTION_QUEUES.filter((q) => !q.superAdminOnly);
  return [];
};

export interface AttentionItem extends AttentionQueue {
  count: number;
}

export interface AttentionSummary {
  /** Queues with something in them, in queue order. Empty queues are left out. */
  items: AttentionItem[];
  /**
   * True when a queue this role owns could not be counted. The strip says so rather than
   * reporting "nothing needs attention" over a count it never got.
   */
  incomplete: boolean;
}

/**
 * What to show for a role, given the counts that came back.
 *
 * A count of null or undefined is a queue that failed to load; zero is a queue that is clear.
 * Only the role's own queues are read, so a count for a queue the role may not see is ignored
 * even if a caller fetched it.
 */
export const needsAttention = (
  role: string | null | undefined,
  counts: Partial<Record<AttentionKey, number | null>>,
): AttentionSummary => {
  const items: AttentionItem[] = [];
  let incomplete = false;
  for (const queue of queuesFor(role)) {
    const count = counts[queue.key];
    if (count == null || !Number.isFinite(count)) {
      incomplete = true;
      continue;
    }
    if (count > 0) items.push({ ...queue, count });
  }
  return { items, incomplete };
};

/** The UTC day the import page opens on, as the bounds it sends. */
export const importDayBounds = (now: Date = new Date()): { from: string; to: string } => {
  const day = now.toISOString().slice(0, 10);
  return {
    from: new Date(`${day}T00:00:00.000Z`).toISOString(),
    to: new Date(`${day}T23:59:59.999Z`).toISOString(),
  };
};
